import { addWeeks, differenceInWeeks, getWeek, getWeekYear, parse, startOfWeek, subDays, subWeeks } from 'date-fns';
import type { DateRange } from '../../types';
import { WEEKLY_START_DAY_OPTIONS } from '../../types';
import type {
  WeeklyComparisonPresetId,
  WeeklyPresetId,
  MainPresetDefinition,
  ComparisonPresetDefinition,
} from '../types';

export const WEEKLY_PRESET_REGISTRY: Record<WeeklyPresetId, MainPresetDefinition<WeeklyPresetId>> = {
  custom: {
    id: 'custom',
    labelKey: 'datepicker.options.main.custom',
    calculate: () => {
      const now = new Date();
      return {
        from: startOfWeek(subWeeks(now, 1), WEEKLY_START_DAY_OPTIONS),
        to: subDays(startOfWeek(now, WEEKLY_START_DAY_OPTIONS), 1),
      };
    },
  },
  last_week: {
    id: 'last_week',
    labelKey: 'datepicker.options.main.last.week',
    calculate: () => {
      const now = new Date();
      return {
        from: startOfWeek(subWeeks(now, 1), WEEKLY_START_DAY_OPTIONS),
        to: subDays(startOfWeek(now, WEEKLY_START_DAY_OPTIONS), 1),
      };
    },
  },
  last_4_weeks: {
    id: 'last_4_weeks',
    labelKey: 'datepicker.options.main.last.four.weeks',
    calculate: () => {
      const now = new Date();
      return {
        from: startOfWeek(subWeeks(now, 4), WEEKLY_START_DAY_OPTIONS),
        to: subDays(startOfWeek(now, WEEKLY_START_DAY_OPTIONS), 1),
      };
    },
  },
  last_12_weeks: {
    id: 'last_12_weeks',
    labelKey: 'datepicker.options.main.last.twelve.weeks',
    calculate: () => {
      const now = new Date();
      return {
        from: startOfWeek(subWeeks(now, 12), WEEKLY_START_DAY_OPTIONS),
        to: subDays(startOfWeek(now, WEEKLY_START_DAY_OPTIONS), 1),
      };
    },
  },
};

export const WEEKLY_COMPARISON_REGISTRY: Record<
  WeeklyComparisonPresetId,
  ComparisonPresetDefinition<WeeklyComparisonPresetId>
> = {
  custom: {
    id: 'custom',
    labelKey: 'datepicker.options.main.custom',
    calculate: () => undefined,
  },
  previous_period: {
    id: 'previous_period',
    labelKey: 'datepicker.options.comparison.previous.period',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      const baseStart = startOfWeek(new Date(mainRange.from), WEEKLY_START_DAY_OPTIONS);
      const diffWeeks = differenceInWeeks(new Date(mainRange.to), new Date(mainRange.from)) + 1;
      return {
        from: subWeeks(baseStart, diffWeeks),
        to: subDays(baseStart, 1),
      };
    },
  },
  previous_year: {
    id: 'previous_year',
    labelKey: 'datepicker.options.comparison.previous.year.week',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      const baseStart = startOfWeek(new Date(mainRange.from), WEEKLY_START_DAY_OPTIONS);
      const diffWeeks = differenceInWeeks(new Date(mainRange.to), new Date(mainRange.from)) + 1;
      const calendarWeek = getWeek(baseStart, WEEKLY_START_DAY_OPTIONS);
      const weekYear = getWeekYear(baseStart, WEEKLY_START_DAY_OPTIONS);
      const startDateLastYear = parse(calendarWeek.toString(), 'I', new Date().setFullYear(weekYear - 1));
      return {
        from: startDateLastYear,
        to: subDays(addWeeks(startDateLastYear, diffWeeks), 1),
      };
    },
  },
};
